import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Download, MessageSquare, FileText } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

interface MultiDocAnalysisDisplayProps {
  result: any;
  historyItem?: any; // full history record for user_prompt + resource_ids
  theme?: 'light' | 'dark';
  showHeader?: boolean;
  showToggle?: boolean;
}

interface Section {
  title: string;
  content: string;
}

function formatResourceId(id: string): string {
  if (!id) return id;
  if (id.startsWith('patent-')) return `US Patent ${id.slice('patent-'.length)}`;
  if (id.startsWith('publication-')) return `US Publication ${id.slice('publication-'.length)}`;
  if (id.startsWith('foreign_patent-')) return id.slice('foreign_patent-'.length);
  return id;
}

/** Split analysis text into per-document sections based on ## headers */
function parseSections(text: string): Section[] {
  const lines = text.split('\n');
  const sections: Section[] = [];
  let currentTitle = '';
  let currentLines: string[] = [];

  for (const line of lines) {
    if (line.match(/^##\s+/)) {
      if (currentTitle || currentLines.some(l => l.trim())) {
        sections.push({ title: currentTitle, content: currentLines.join('\n').trim() });
      }
      currentTitle = line.replace(/^##\s*/, '').trim();
      currentLines = [];
    } else {
      currentLines.push(line);
    }
  }
  if (currentTitle || currentLines.some(l => l.trim())) {
    sections.push({ title: currentTitle, content: currentLines.join('\n').trim() });
  }

  return sections;
}

function renderInlineBold(text: string, theme: 'light' | 'dark') {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function renderMarkdown(text: string, theme: 'light' | 'dark') {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('### ')) {
      return (
        <h4 key={i} className={`text-sm font-semibold mt-3 mb-1 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
          {trimmed.slice(4)}
        </h4>
      );
    }
    if (trimmed.startsWith('# ')) {
      return (
        <h3 key={i} className={`text-base font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
          {trimmed.slice(2)}
        </h3>
      );
    }
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      return (
        <li key={i} className={`ml-5 my-0.5 leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          {renderInlineBold(trimmed.slice(2), theme)}
        </li>
      );
    }
    if (/^\d+\.\s/.test(trimmed)) {
      return (
        <div key={i} className={`font-medium mt-3 mb-1 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
          {renderInlineBold(trimmed, theme)}
        </div>
      );
    }
    if (!trimmed) {
      return <div key={i} className="h-1.5" />;
    }
    return (
      <p key={i} className={`my-1.5 leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
        {renderInlineBold(line, theme)}
      </p>
    );
  });
}

function SectionCard({
  section,
  index,
  theme,
  defaultOpen,
}: {
  section: Section;
  index: number;
  theme: 'light' | 'dark';
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`rounded-lg border ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors rounded-lg ${
          theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
        } ${open ? (theme === 'dark' ? 'rounded-b-none' : 'rounded-b-none border-b border-gray-200') : ''}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className={`w-4 h-4 shrink-0 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
          <span className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
            {section.title || `Document ${index + 1}`}
          </span>
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        }
      </button>
      {open && (
        <div className={`px-4 pb-4 pt-2 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="space-y-0.5">
            {renderMarkdown(section.content, theme)}
          </div>
        </div>
      )}
    </div>
  );
}

const MultiDocAnalysisDisplay: React.FC<MultiDocAnalysisDisplayProps> = ({
  result,
  historyItem,
  theme = 'light',
  showHeader = true,
  showToggle = false,
}) => {
  const [showRaw, setShowRaw] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);

  if (!result || typeof result !== 'string') return null;

  const analysisText = result;
  const userPrompt: string | undefined = historyItem?.user_prompt;
  const resourceIds: string[] = Array.isArray(historyItem?.resource_ids)
    ? historyItem.resource_ids
    : [];

  const sections = parseSections(analysisText);
  const hasHierarchy = sections.length > 1 || (sections.length === 1 && sections[0].title);

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      children.push(
        new Paragraph({
          text: 'Multi-Document Analysis',
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        })
      );

      if (userPrompt) {
        children.push(
          new Paragraph({
            text: `Question: ${userPrompt}`,
            spacing: { after: 300 },
          })
        );
      }

      if (resourceIds.length > 0) {
        children.push(
          new Paragraph({
            text: `Documents: ${resourceIds.map(formatResourceId).join(', ')}`,
            spacing: { after: 400 },
          })
        );
      }

      const lines = analysisText.split('\n');
      lines.forEach((line: string) => {
        const trimmed = line.trim();
        if (!trimmed) {
          children.push(new Paragraph({ text: '', spacing: { after: 80 } }));
        } else if (trimmed.startsWith('## ')) {
          children.push(new Paragraph({ text: trimmed.slice(3), heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 150 } }));
        } else if (trimmed.startsWith('### ')) {
          children.push(new Paragraph({ text: trimmed.slice(4), heading: HeadingLevel.HEADING_3, spacing: { before: 200, after: 100 } }));
        } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          children.push(new Paragraph({ text: `• ${trimmed.slice(2)}`, spacing: { after: 50 }, indent: { left: 360 } }));
        } else {
          const parts = trimmed.split(/(\*\*[^*]+\*\*)/);
          const runs: TextRun[] = parts.map(p =>
            p.startsWith('**') && p.endsWith('**')
              ? new TextRun({ text: p.slice(2, -2), bold: true })
              : new TextRun({ text: p })
          );
          children.push(new Paragraph({ children: runs, spacing: { after: 100 } }));
        }
      });

      const doc = new Document({ sections: [{ properties: {}, children }] });
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'multi-doc-analysis.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting multi-doc analysis:', err);
    }
  };

  return (
    <div className={showHeader ? 'space-y-3' : ''}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Multi-Document Analysis
          </div>
          {showToggle && (
            <div className="flex items-center gap-2">
              <button
                onClick={exportToDocx}
                className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                  theme === 'dark'
                    ? 'bg-green-600 text-white hover:bg-green-500'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => setShowRaw(r => !r)}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showRaw
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {showRaw ? 'Show Formatted' : 'Show Raw'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Metadata header: question + documents */}
      <div className={`rounded-lg border p-3 space-y-2 ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
        {userPrompt && (
          <div className="flex items-start gap-2">
            <MessageSquare className={`w-4 h-4 shrink-0 mt-0.5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
            <div className="min-w-0 flex-1">
              <p className={`text-sm ${promptExpanded ? 'whitespace-pre-wrap' : 'line-clamp-3'} ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                {userPrompt}
              </p>
              <button
                onClick={() => setPromptExpanded(e => !e)}
                className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                {promptExpanded ? 'Show less' : 'Show all'}
              </button>
            </div>
          </div>
        )}
        {resourceIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {resourceIds.map((id, i) => (
              <Badge key={i} variant="outline" className={`text-xs ${theme === 'dark' ? 'border-gray-500 text-gray-300' : 'border-blue-300 text-blue-700 bg-white'}`}>
                {formatResourceId(id)}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Analysis content */}
      <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
        {showRaw ? (
          <pre className={`text-sm whitespace-pre-wrap font-mono p-4 rounded-md ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>
            {analysisText}
          </pre>
        ) : hasHierarchy ? (
          <div className="space-y-2">
            {sections.map((section, i) => (
              <SectionCard
                key={i}
                section={section}
                index={i}
                theme={theme}
                defaultOpen={i === 0}
              />
            ))}
          </div>
        ) : (
          <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="space-y-0.5">
              {renderMarkdown(analysisText, theme)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiDocAnalysisDisplay;
